import { useEffect, useRef } from 'react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { mascaraDaRolagem, mostrarNaRolagem, useRolagemHorizontal } from '../core/rolagem';
import { SetaDeRolagem } from '../core/SetaDeRolagem';

export interface TabItem {
    id: string;
    label: ReactNode;
    /** Contador exibido ao lado do rótulo. */
    count?: number | null;
}

export interface TabsProps {
    tabs?: TabItem[];
    activeId?: string;
    onChange?: (id: string) => void;
    /** Nome do conjunto de abas para leitores de tela. */
    ariaLabel?: string;
    style?: CSSProperties;
}

/* O que as setas do teclado fazem numa lista de abas (padrão WAI-ARIA). */
const DESTINO: Record<string, (atual: number, total: number) => number> = {
    ArrowRight: (atual, total) => (atual + 1) % total,
    ArrowLeft: (atual, total) => (atual - 1 + total) % total,
    Home: () => 0,
    End: (_atual, total) => total - 1,
};

export function Tabs({ tabs = [], activeId, onChange, ariaLabel, style }: TabsProps) {
    const lista = useRef<HTMLDivElement | null>(null);
    const rolagem = useRolagemHorizontal(lista, tabs.length);

    /* A aba ativa sempre à vista. Entrar direto em "Etiquetas", a última de
       Configurações, deixava a aba acesa fora da tela — a pessoa não via onde
       estava. */
    useEffect(() => {
        const el = lista.current;
        const ativa = el?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
        if (el && ativa) mostrarNaRolagem(el, ativa);
    }, [activeId, tabs.length]);

    /* Ativação manual: a seta leva o foco, Enter ou Espaço abrem. Cada aba
       troca de rota e carrega dados — abrir a cada seta faria quem percorre as
       abas pelo teclado disparar uma carga por passo. */
    const aoTeclar = (evento: KeyboardEvent<HTMLButtonElement>) => {
        const destino = DESTINO[evento.key];
        const el = lista.current;
        if (!destino || !el) return;
        const botoes = [...el.querySelectorAll<HTMLButtonElement>('[role="tab"]')];
        const alvo = botoes[destino(botoes.indexOf(evento.currentTarget), botoes.length)];
        if (!alvo) return;
        evento.preventDefault();
        alvo.focus({ preventScroll: true });
        mostrarNaRolagem(el, alvo);
    };

    return (
        <div style={{ position: 'relative', minWidth: 0, maxWidth: '100%', ...style }}>
            <div
                ref={lista}
                role="tablist"
                aria-label={ariaLabel}
                data-rolagem="horizontal"
                style={{
                    position: 'relative', display: 'flex', gap: 20, flexWrap: 'nowrap',
                    overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none',
                    borderBottom: '1px solid var(--border-subtle)', ...mascaraDaRolagem(rolagem),
                }}
            >
                <style>{'[role=tablist]::-webkit-scrollbar{height:0;display:none}'}</style>
                {tabs.map((t) => {
                    const ativa = t.id === activeId;
                    return (
                        <button
                            key={t.id} type="button" role="tab" aria-selected={ativa}
                            tabIndex={ativa ? 0 : -1}
                            onClick={() => onChange?.(t.id)}
                            onKeyDown={aoTeclar}
                            style={{
                                border: 0, background: 'transparent', cursor: 'pointer', padding: '0 0 10px',
                                flex: '0 0 auto', whiteSpace: 'nowrap',
                                fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: ativa ? 600 : 500,
                                color: ativa ? 'var(--text-brand)' : 'var(--text-secondary)',
                                boxShadow: ativa ? 'inset 0 -2px 0 var(--burgundy-700)' : 'none',
                                display: 'flex', alignItems: 'center', gap: 7, transition: 'var(--transition-control)',
                            }}
                        >
                            {t.label}
                            {t.count != null && (
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: ativa ? 'var(--text-brand)' : 'var(--text-tertiary)', background: ativa ? 'var(--surface-brand-soft)' : 'var(--surface-secondary)', borderRadius: 'var(--radius-pill)', padding: '1px 7px' }}>
                                    {t.count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            {/* A seta fica acima da linha de abas, não da borda inferior: 10 px
                de respiro da aba descontados do centro. */}
            {rolagem.ponteiroFino && rolagem.antes && <SetaDeRolagem lado="antes" topo="calc(50% - 5px)" onClick={() => rolagem.rolar(-1)} />}
            {rolagem.ponteiroFino && rolagem.depois && <SetaDeRolagem lado="depois" topo="calc(50% - 5px)" onClick={() => rolagem.rolar(1)} />}
        </div>
    );
}
